import {
  getSessionStorageItem,
  SESSION_STORAGE_KEYS,
  setSessionStorageItem,
} from '@chegg-wt/core/utils/sessionStorage'
import { AuthModalOrigin } from '@chegg-wt/core/components/Modals/AuthModal/types'
import { closeModal, openModal } from '@chegg-wt/core/state/modals/actions'
import { EXPORT_MODAL } from '@chegg-wt/core/state/modals/constants'
import { getCitationsFromSessionStorage } from '@chegg-wt/core/utils/writingForms'
import {
  formPageAuthComplete,
  writingFormSubmit,
} from '@chegg-wt/core/state/form/actions'
import { AuthSuccessGrant } from '@chegg-wt/core/state/user/logics'
import { useDispatch, useSelector } from 'react-redux'
import { login } from '@chegg-wt/core/state/user/actions'
import { useEffect, useState } from 'react'

import {
  accessTokenSelector,
  isAuthLoadingSelector,
  userRoleSelector,
} from '@chegg-wt/core/state/user/selectors'
import { useRouter } from 'next/router'
import { UserRoles } from '@chegg-wt/core/constants/user'
import { currentProjectIdSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { Cookies, getCookie } from '@chegg-wt/core/utils/cookies'

export const useAuthenticationHook = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const { authSuccess, origin, alwaysParticipate } = router.query as {
    authSuccess: string
    origin: string
    alwaysParticipate: string
  }

  const [isLoginComplete, setIsLoginComplete] = useState(false) // ✅ Track login completion

  // ✅ FIRST: Handle user login before anything else
  useEffect(() => {
    if (authSuccess === 'true') {
      const successResponse: AuthSuccessGrant = {
        id_token: getCookie(Cookies.IdToken),
        access_token: getCookie(Cookies.access_token),
      }

      dispatch(login(successResponse))
      // ✅ Mark login as completed
      setIsLoginComplete(true)
    }
  }, [])

  // Get user-related state
  const userRole = useSelector(userRoleSelector)
  const isLoad = useSelector(isAuthLoadingSelector)
  const idToken = useSelector(accessTokenSelector)
  const [idTokenAvailable, setIdTokenAvailable] = useState(false)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  // Auth success state
  const [isAuthSuccess, setIsAuthSuccess] = useState(false)
  const [isProcessed, setIsProcessed] = useState(false)
  const projectId = useSelector(currentProjectIdSelector)
  const [isProjectIdAvailable, setIsProjectIdAvailable] = useState(false)
  useEffect(() => {
    setIsProjectIdAvailable(true)
  }, [projectId])

  useEffect(() => {
    setIsAuthLoading(false)
  }, [isLoad])

  useEffect(() => {
    setIdTokenAvailable(true)
  }, [idToken])
  // Wait until `authSuccess` is available
  useEffect(() => {
    if (authSuccess === 'true') {
      setIsAuthSuccess(true)
    }
  }, [authSuccess])
  // ✅ SECOND: Handle post-login actions (only after login is complete)
  useEffect(() => {
    if (!isLoginComplete || !userRole || userRole === UserRoles.NOT_LOGGED_IN) {
      return
    }

    if (
      !isProcessed &&
      userRole &&
      isProjectIdAvailable &&
      projectId &&
      idTokenAvailable &&
      !isAuthLoading
    ) {
      switch (origin) {
        case AuthModalOrigin.VIEW_PAGE_EXPORT: {
          const selectedCitations = JSON.parse(
            getSessionStorageItem(SESSION_STORAGE_KEYS.selectedCitations)
          )
          dispatch(openModal({ name: EXPORT_MODAL, data: { selectedCitations } }))
          break
        }

        case AuthModalOrigin.CITATION_FORM_PAGE: {
          const writingFormsCitationData = getCitationsFromSessionStorage()
          const { citation, initialCitationData } = writingFormsCitationData
          dispatch(writingFormSubmit({ citation, initialCitationData }))
          break
        }

        case AuthModalOrigin.REDESIGNED_UNLOCK_MODAL: {
          dispatch(
            formPageAuthComplete({ alwaysParticipate: alwaysParticipate === 'true' })
          )

          break
        }

        default:
          break
      }
      setIsProcessed(true)

      if (origin === AuthModalOrigin.UPGRADE_MODAL && userRole === UserRoles.PRO_USER) {
        dispatch(closeModal(origin))
      }
      setSessionStorageItem(SESSION_STORAGE_KEYS.authSuccess, 'true')
      setSessionStorageItem(SESSION_STORAGE_KEYS.role, userRole)
    }
  }, [
    userRole,
    isAuthSuccess,
    isLoginComplete,
    isProjectIdAvailable,
    projectId,
    idTokenAvailable,
    isAuthLoading,
  ])
}
